import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle2, Phone, Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Section, Button } from '../components/ui/Common.jsx';
import ResponsiveImage from '../components/ui/ResponsiveImage.jsx';
import { services, companyInfo } from '../data.js';
import { cn } from '../lib/utils.js';

export default function Home() {
  return (
    <>
      <Helmet>
        <title>Sydthy Taxi & Turistfart ApS | Professionel transport i Thy & Mors</title>
        <meta name="description" content="Sydthy Taxi & Turistfart tilbyder professionel minibuskørsel, turistkørsel og taxa i Thy og på Mors. Lokalt forankret med fokus på kvalitet." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-screen lg:min-h-[800px] w-full overflow-hidden flex items-center">
        <div className="absolute inset-0 z-0">
          <ResponsiveImage
            src="https://assets.auxodev.dk/assets/80af3c4d-4953-436f-a9f5-4abfeafc9910/1f806915-39a6-4028-924d-004fb5b9df37.jpg"
            alt="Sydthy Minibus i Thy landskab"
            className="h-full w-full object-cover"
            sizes="100vw"
          />
          <div className="absolute inset-0 bg-slate-900/40" />
        </div>

        <div className="container relative z-10 mx-auto px-4 py-32 md:px-6">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <span className="mb-4 inline-block text-sm font-semibold uppercase tracking-[0.2em] text-white/80">
                Velkommen til Sydthy Taxi & Turistfart
              </span>
              <h1 className="mb-6 text-[2.5rem] font-bold leading-[1.1] text-white md:text-7xl">
                Professionel transport <br />
                <span className="text-blue-400">med lokalt hjerte</span>
              </h1>
              <p className="mb-10 text-xl text-white/90 leading-relaxed max-w-2xl">
                Vi forener moderne komfort med nordjysk pålidelighed. Uanset om det er minibus, turistkørsel eller taxa, sørger vi for en tryg rejse.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/kontakt">
                  <Button variant="blue" size="lg" className="gap-2">
                    <Calendar className="h-5 w-5" />
                    Book transport
                  </Button>
                </Link>
                <a href={`tel:${companyInfo.phone.replace(/\s/g, '')}`}>
                  <Button variant="outline" size="lg" className="gap-2">
                    <Phone className="h-5 w-5" />
                    Ring nu
                  </Button>
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <Section padding="compact" className="bg-slate-50 border-y border-slate-100">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="mb-6 text-3xl font-bold md:text-4xl lg:text-5xl">
            "Så bliver det ikke mere lokalt"
          </h2>
          <div className="space-y-6 text-lg text-slate-600 leading-relaxed">
            <p>
              Sydthy Taxi & Turistfart ApS er en familiedrevet virksomhed med base i Hurup Thy. Vi værner om værdier som personlig service og glade passagerer.
            </p>
            <p>
              Siden starten i 2021 har vi løbende udvidet vores vognpark, så vi i dag råder over alt fra taxier og minibusser med lift til store turistbusser. Vi står klar til at levere en professionel og behagelig service, uanset jeres behov.
            </p>
          </div>
          <div className="mt-10">
            <Link to="/om-os">
              <Button variant="primary" className="gap-2">
                Læs mere om os <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </Section>

      {/* Services Grid */}
      <Section className="bg-white">
        <div className="mb-16 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="mb-4 text-4xl font-bold tracking-tight text-slate-900 md:text-5xl">Vores Ydelser</h2>
            <div className="mx-auto mb-6 h-1 w-20 bg-blue-600" />
            <p className="mx-auto max-w-2xl text-lg text-slate-600">
              Vi dækker alle transportbehov med vores alsidige vognpark og erfarne chauffører. 
              Fra den hurtige taxa til den store turistbus.
            </p>
          </motion.div>
        </div>

        <div className="grid gap-8 md:grid-cols-2 max-w-5xl mx-auto">
          {services.map((service, index) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="flex flex-col bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden group"
            >
              {/* Image Container */}
              <div className="relative aspect-[16/10] overflow-hidden bg-slate-100">
                <ResponsiveImage
                  src={service.image}
                  alt={service.title}
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  sizes="(max-width: 768px) 100vw, 500px"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/40 to-transparent" />
                <span className="absolute top-4 left-4 bg-blue-600 text-white text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full shadow-sm">
                  {service.id === 'turist' ? 'Bus & Turist' : '24/7 Service'}
                </span>
              </div>

              {/* Content Container */}
              <div className="flex flex-col flex-grow p-6 md:p-8">
                <h3 className="text-2xl font-bold text-slate-900 mb-3 group-hover:text-blue-600 transition-colors">
                  {service.title}
                </h3>
                <p className="text-slate-600 mb-6 leading-relaxed">
                  {service.description}
                </p>

                {/* Features List */}
                {service.features && (
                  <div className="mt-auto border-t border-slate-100 pt-6 mb-8">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-4">
                      Hvad vi tilbyder:
                    </h4>
                    <ul className="grid grid-cols-2 gap-3">
                      {service.features.map((feature, fIdx) => (
                        <li key={fIdx} className="flex items-center gap-2 text-sm text-slate-700">
                          <CheckCircle2 className="h-4 w-4 text-blue-500 shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Action Button */}
                <Link
                  to={service.id === 'turist' ? '/ydelser/turist' : '/taxa'}
                  className="inline-flex items-center justify-center gap-2 w-full bg-slate-50 hover:bg-blue-600 text-slate-800 hover:text-white font-semibold py-3 px-4 rounded-xl border border-slate-200 hover:border-blue-600 transition-all duration-200 group/btn"
                >
                  Udforsk {service.title.toLowerCase()}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover/btn:translate-x-1" />
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* Why Us Section */}
      <Section dark>
        <div className="grid gap-16 lg:grid-cols-3">
          <div className="lg:col-span-1">
            <h2 className="mb-6 text-3xl font-bold md:text-4xl">Hvorfor vælge os?</h2>
            <p className="text-slate-400 leading-relaxed">
              Vi kombinerer lokal kendskab med en moderne tilgang til transport, hvilket sikrer dig den bedste oplevelse hver gang.
            </p>
          </div>
          <div className="lg:col-span-2 grid gap-8 sm:grid-cols-2">
            {[
              { title: "Lokalt forankret", desc: "Vi kender hver en vej i Thy og på Mors." },
              { title: "Moderne flåde", desc: "Vores biler er nye, rene og velvedligeholdte." },
              { title: "Sikkerhed først", desc: "Alle chauffører er certificerede og erfarne." },
              { title: "Fleksibilitet", desc: "Vi tilpasser os dine specifikke behov og ønsker." }
            ].map((item, idx) => (
              <div key={idx} className="flex gap-4">
                <CheckCircle2 className="h-6 w-6 shrink-0 text-blue-500" />
                <div>
                  <h4 className="mb-2 font-bold text-white">{item.title}</h4>
                  <p className="text-sm text-slate-400">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* CTA Section */}
      <Section className="bg-blue-600 text-white text-center">
        <div className="max-w-3xl mx-auto">
          <h2 className="mb-6 text-4xl font-bold">Klar til at bestille din næste tur?</h2>
          <p className="mb-10 text-xl text-blue-100">
            Kontakt os i dag for et uforpligtende tilbud på din transportopgave.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/kontakt">
              <Button variant="secondary" size="lg">
                Kontakt os nu
              </Button>
            </Link>
            <a href={`tel:${companyInfo.phone.replace(/\s/g, '')}`}>
              <Button variant="outline" size="lg" className="border-white/50 hover:bg-white/10">
                Ring {companyInfo.phone}
              </Button>
            </a>
          </div>
        </div>
      </Section>
    </>
  );
}
